import { PropsWithChildren, FC } from 'react';
import { Box, Stack } from '@mui/material';
import { theme } from '@vs-online-only-app/ui';

type ChatBubbleColor = 'primary' | 'secondary';
export type ChatBubblePlacement = 'left' | 'right';
interface BorderRadiusConfig {
  direction: ChatBubblePlacement;
  defaultRadius: string;
  isStacked: boolean;
  isFirst: boolean;
  isLast: boolean;
}

// Determines the border radius for items in a stack based on position and direction
function calculateFirstOrLastBorderRadius(config: BorderRadiusConfig): string {
  const { direction, defaultRadius, isFirst, isLast } = config;

  const topLeft = isLast && direction === 'left' ? '0px' : defaultRadius;
  const topRight = isLast && direction === 'right' ? '0px' : defaultRadius;
  const bottomRight = isFirst && direction === 'right' ? '0px' : defaultRadius;
  const bottomLeft = isFirst && direction === 'left' ? '0px' : defaultRadius;

  return `${topLeft} ${topRight} ${bottomRight} ${bottomLeft}`;
}

function calculateMiddleBorderRadius(config: BorderRadiusConfig): string {
  const { direction, defaultRadius } = config;

  const topLeft = direction === 'left' ? '0px' : defaultRadius;
  const topRight = direction === 'right' ? '0px' : defaultRadius;
  const bottomRight = direction === 'right' ? '0px' : defaultRadius;
  const bottomLeft = direction === 'left' ? '0px' : defaultRadius;

  return `${topLeft} ${topRight} ${bottomRight} ${bottomLeft}`;
}

function getBorderRadius(config: BorderRadiusConfig): string {
  const { defaultRadius, isStacked, isFirst, isLast } = config;

  // Standalone item, full radius for all corners
  if (!isStacked) return defaultRadius;

  // First or last item in a stack
  if (isFirst || isLast) {
    return calculateFirstOrLastBorderRadius(config);
  }

  // Middle item in a stack
  return calculateMiddleBorderRadius(config);
}

function getBorderColor(borderColor?: ChatBubbleColor) {
  const borderStyle = '1px solid';

  if (borderColor === 'primary') {
    return `${borderStyle} ${theme.palette.primary.main}`;
  }
  if (borderColor === 'secondary') {
    return `${borderStyle} ${theme.palette.divider}`;
  }
  return undefined;
}

function getBackground(backgroundColor?: ChatBubbleColor) {
  if (backgroundColor === 'primary') {
    return theme.palette.primary.main;
  }
  if (backgroundColor === 'secondary') {
    return theme.palette.background.default;
  }
  return 'transparent';
}

function getColor(backgroundColor?: ChatBubbleColor) {
  if (backgroundColor === 'primary') {
    return theme.palette.primary.contrastText;
  }
  return theme.palette.text.primary;
}

function getColors(
  backgroundColor?: ChatBubbleColor,
  borderColor?: ChatBubbleColor,
) {
  const background = getBackground(backgroundColor);
  const border = getBorderColor(borderColor);
  const color = getColor(backgroundColor);

  return { background, border, color };
}

type Props = {
  direction: ChatBubblePlacement;
  isLastInStack?: boolean;
  isFirstInStack?: boolean;
  isStacked?: boolean;
  backgroundColor?: ChatBubbleColor;
  borderColor?: ChatBubbleColor;
  maxWidthPercentage?: number;
  minWidthPercentage?: number;
  noPadding?: boolean;
};

const ChatBubble: FC<PropsWithChildren<Props>> = ({
  direction,
  children,
  backgroundColor,
  isFirstInStack,
  isLastInStack,
  isStacked,
  maxWidthPercentage,
  minWidthPercentage,
  borderColor,
  noPadding,
}) => {
  return (
    <>
      <Stack
        direction="row"
        justifyContent={direction === 'left' ? 'flex-start' : 'flex-end'}
      >
        <Box
          sx={(theme) => ({
            padding: noPadding ? 0 : theme.spacing(1.5, 2),
            minWidth: minWidthPercentage && `${minWidthPercentage}%`,
            maxWidth: `${maxWidthPercentage || 100}%`,
            borderRadius: getBorderRadius({
              direction,
              defaultRadius: `${theme.shape.borderRadius * 2}px`,
              isStacked: Boolean(isStacked),
              isFirst: Boolean(isFirstInStack),
              isLast: Boolean(isLastInStack),
            }),
            position: 'relative',
            ...getColors(backgroundColor, borderColor),
          })}
        >
          {children}
        </Box>
      </Stack>
      <Box
        height={(theme) =>
          isLastInStack || !isStacked ? theme.spacing(1.5) : theme.spacing(0.5)
        }
      />
    </>
  );
};

export default ChatBubble;
